import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, Compass, Share2 } from 'lucide-react';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { TreeRingsHero } from '../components/TreeRingsHero';
export function Values() {
  const values = [{
    title: 'CONVICTION',
    desc: 'Fuel startups that are creating solutions inspired by and respecting the intelligence of nature',
    icon: Heart,
    color: '#78e1c4',
    position: 'top-0 left-0 md:left-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(20% 0%, 80% 0%, 100% 30%, 95% 70%, 75% 100%, 25% 100%, 5% 70%, 0% 30%)'
  }, {
    title: 'ATHLETICISM',
    desc: 'Approach big challenges like training—balancing mindset, grit, discipline, and recovery to navigate the unknowns and support peak performance',
    icon: Activity,
    color: '#4DE7D4',
    position: 'top-0 right-0 md:right-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 70% 100%, 20% 100%, 0% 75%, 5% 25%)'
  }, {
    title: 'ADVENTURE',
    desc: 'Engage new ideas and bold ventures with intellectual curiosity and a spirit of exploration',
    icon: Compass,
    color: '#a0e6e9',
    position: 'bottom-0 left-0 md:left-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(25% 0%, 75% 0%, 95% 30%, 100% 70%, 80% 100%, 20% 100%, 0% 70%, 5% 30%)'
  }, {
    title: 'CONNECTION',
    desc: 'Work across disciplines, sectors, and industries to create partnerships, insights, and breakthroughs',
    icon: Share2,
    color: '#edcd99',
    position: 'bottom-0 right-0 md:right-[5%]',
    size: 'w-full md:w-[45%]',
    clipPath: 'polygon(20% 0%, 80% 0%, 100% 25%, 95% 75%, 75% 100%, 25% 100%, 0% 75%, 5% 25%)'
  }];
  return <div className="min-h-screen pt-20" data-id="element-1746">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden min-h-[50vh] flex items-center" data-id="element-1747">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] via-[#1c3f4d] to-[#0e2533] z-0" data-id="element-1748" />

        {/* Background image - tree rings close-up */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-25" data-id="element-1749" />

        {/* Subtle SVG overlay */}
        <div className="absolute inset-0 opacity-20" data-id="element-1750">
          <TreeRingsHero data-id="element-1751" />
        </div>

        <div className="absolute inset-0 bg-carbonBlack/40" data-id="element-1752" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center w-full" data-id="element-1753">
          <motion.h1 initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="text-6xl md:text-8xl font-bold text-white tracking-tight" data-id="element-1754">
            VALUES
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-vitalityTeal mt-4" data-id="element-1755">
            The living principles that guide our work
          </motion.p>
        </div>
      </section>

      {/* Cellular Grid / Organic Tessellation */}
      <section className="py-32 bg-[#0e2533] relative overflow-hidden" data-id="element-1756">
        {/* Background cellular pattern */}
        <div className="absolute inset-0 opacity-5" data-id="element-1757">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-1758">
            <defs data-id="element-1759">
              <pattern id="cellPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse" data-id="element-1760">
                <circle cx="50" cy="50" r="30" fill="none" stroke="#78e1c4" strokeWidth="1" opacity="0.3" data-id="element-1761" />
                <circle cx="50" cy="50" r="20" fill="none" stroke="#4DE7D4" strokeWidth="1" opacity="0.2" data-id="element-1762" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cellPattern)" data-id="element-1763" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1764">
          {/* Organic cellular grid */}
          <div className="relative min-h-[1200px] md:min-h-[900px]" data-id="element-1765">
            {values.map((value, index) => <motion.div key={value.title} initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: index * 0.15
          }} className={`absolute ${value.position} ${value.size} p-4`} style={{
            top: index < 2 ? '0' : 'auto',
            bottom: index >= 2 ? '0' : 'auto'
          }} data-id="element-1766">
                {/* Cellular glow */}
                <motion.div className="absolute -inset-4 blur-2xl opacity-30" style={{
              backgroundColor: value.color,
              clipPath: value.clipPath
            }} animate={{
              opacity: [0.2, 0.4, 0.2],
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 4 + index,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-1767" />

                {/* Cell membrane (outer border) */}
                <div className="relative bg-[#1c3f4d]/40 backdrop-blur-sm border-2 overflow-hidden" style={{
              clipPath: value.clipPath,
              borderColor: `${value.color}60`,
              minHeight: '400px'
            }} data-id="element-1768">
                  {/* Cellular texture overlay */}
                  <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `radial-gradient(circle at 30% 40%, ${value.color} 1px, transparent 1px),
                                       radial-gradient(circle at 70% 60%, ${value.color} 1px, transparent 1px)`,
                backgroundSize: '30px 30px, 40px 40px'
              }} data-id="element-1769" />

                  {/* Breathing animation overlay */}
                  <motion.div className="absolute inset-0" style={{
                backgroundColor: `${value.color}10`
              }} animate={{
                opacity: [0.1, 0.3, 0.1]
              }} transition={{
                duration: 3 + index * 0.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1770" />

                  {/* Content */}
                  <div className="relative z-10 p-8 md:p-10 flex flex-col items-center text-center h-full justify-center" data-id="element-1771">
                    {/* Nucleus (icon container) */}
                    <motion.div className="relative mb-6" animate={{
                  rotate: [0, 5, -5, 0]
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1772">
                      <div className="w-20 h-20 flex items-center justify-center relative" style={{
                    clipPath: 'circle(50% at 50% 50%)',
                    backgroundColor: `${value.color}30`,
                    border: `2px solid ${value.color}60`
                  }} data-id="element-1773">
                        <value.icon className="w-9 h-9" style={{
                      color: value.color
                    }} data-id="element-1774" />
                      </div>

                      {/* Pulsing membrane */}
                      <motion.div className="absolute inset-0 rounded-full" style={{
                    border: `2px solid ${value.color}`
                  }} animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.6, 0, 0.6]
                  }} transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3
                  }} data-id="element-1775" />
                    </motion.div>

                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 tracking-wide" data-id="element-1776">
                      {value.title}
                    </h3>

                    <p className="text-gray-300 leading-relaxed text-base md:text-lg max-w-md" data-id="element-1777">
                      {value.desc}
                    </p>

                    {/* Cell activity indicator */}
                    <div className="mt-6 flex items-center space-x-2 text-xs" style={{
                  color: value.color
                }} data-id="element-1778"></div>
                  </div>
                </div>

                {/* Cell connections (visible on desktop) */}
                {index < 2 && <motion.div className="hidden md:block absolute top-1/2 -right-8 w-16 h-0.5 z-0" style={{
              backgroundColor: `${value.color}40`
            }} initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.5 + index * 0.2
            }} data-id="element-1779" />}
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}